import { gql } from '@apollo/client';

export const workPackageFragments = {
  quantity: gql`
    fragment Quantity on Quantity {
      quantity
      unitOfMeasure
    }
  `,
  crewMember: gql`
    fragment CrewMember on CrewMember {
      id
    }
  `,
};

export const workPackageQuantityFragment = {
  crewMember: gql`
    fragment CrewMember on CrewMember {
      id
      firstName
      lastName
      persona
      imageUrl
    }
  `,
  quantitySummary: gql`
    fragment QuantitySummary on QuantitySummary {
      plannedHours
      claimedHours
      approvedHours
      plannedQuantity {
        ...Quantity
      }
      claimedQuantity {
        ...Quantity
      }
      approvedQuantity {
        ...Quantity
      }
    }
    ${workPackageFragments.quantity}
  `,
};

export const workpackageOwnersFragments = {
  owner: gql`
    fragment Owners on Owner {
      assignedCrewMembers {
        crewMember {
          ...CrewMember
        }
        persona
      }
      totalQuantity {
        ...QuantitySummary
      }
    }
    ${workPackageQuantityFragment.quantitySummary}
    ${workPackageQuantityFragment.crewMember}
  `,
};

export const GET_WORKPACKAGES = gql`
  query workPackages($filter: WorkPackageFilterInput, $paging: PagingInput) {
    workPackages(input: $filter, paging: $paging) {
      cursor
      total
      items {
        id
        tag
        startDate
        endDate
        numBlocks
        materialsOnSite {
          commodityType {
            id
            name
          }
          materialTracking
          totalCommodities
        }
        earnedHours
        earnedQuantity
        totalEarnableHours
        totalEarnableQuantity {
          ...Quantity
        }
        percentageComplete
        primaryOwners {
          crewMember {
            ...CrewMember
          }
        }
        secondaryOwners {
          crewMember {
            ...CrewMember
          }
        }
        otherOwners {
          crewMember {
            ...CrewMember
          }
        }
      }
    }
    ${workPackageFragments.quantity},
    ${workPackageFragments.crewMember}
  }
`;

export const GET_WORKPACKAGE_STATS_BY_IDS = gql`
  query workPackagesByIds($ids: [ID!]!) {
    workPackagesByIds(ids: $ids) {
      id
      tag
      numBlocks
      materialsOnSite {
        commodityType {
          id
          name
        }
        materialTracking
        totalCommodities
      }
      earnedHours
      totalEarnableHours
      earnedQuantity
      totalEarnableQuantity {
        ...Quantity
      }
    }
    ${workPackageFragments.quantity}
  }
`;

export const GET_WORKPACKAGE_DETAIL_BY_IDS = gql`
  query workPackagesByIds($ids: [ID!]!) {
    workPackagesByIds(ids: $ids) {
      id
      tag
      numBlocks
      availableCommodityTypes {
        id
        name
      }
      lastUpdated
      startDate
      endDate
      plannedMilestoneStartDate
      plannedMilestoneEndDate
      earnedHours
      plannedHours
      totalEarnableHours
      earnedQuantity
      totalEarnableQuantity {
        ...Quantity
      }
      percentageComplete
      materialsOnSite {
        materialTracking
        totalCommodities
        commodityType {
          id
          name
        }
      }
      facilities {
        id
      }
      primaryOwners {
        persona
        crewMember {
          id
          firstName
          lastName
          imageUrl
          shiftType
        }
      }
      secondaryOwners {
        persona
        crewMember {
          id
          firstName
          lastName
          imageUrl
          shiftType
        }
      }
      otherOwners {
        persona
        crewMember {
          id
          firstName
          lastName
          imageUrl
          shiftType
        }
      }
    }
    ${workPackageFragments.quantity}
  }
`;

export const GET_WORKPACKAGE_ADDITIONAL_INFO_BY_IDS = gql`
  query workPackagesByIds($ids: [ID!]!) {
    workPackagesByIds(ids: $ids) {
      id
      tag
      numBlocks
      availableCommodityTypes {
        id
        name
      }
      lastUpdated
      startDate
      endDate
      plannedMilestoneStartDate
      plannedMilestoneEndDate
      earnedHours
      plannedHours
      totalEarnableHours
      earnedQuantity
      totalEarnableQuantity {
        ...Quantity
      }
      percentageComplete
      materialsOnSite {
        materialTracking
        totalCommodities
        commodityType {
          id
          name
        }
      }
      facilities {
        id
      }
      primaryOwners {
        persona
        crewMember {
          id
          firstName
          lastName
          imageUrl
          shiftType
        }
      }
      secondaryOwners {
        persona
        crewMember {
          id
          firstName
          lastName
          imageUrl
          shiftType
        }
      }
      otherOwners {
        persona
        crewMember {
          id
          firstName
          lastName
          imageUrl
          shiftType
        }
      }
    }
    ${workPackageFragments.quantity}
  }
`;

export const GET_WORKPACKAGE_DOCUMENTS_BY_IDS = gql`
  query workPackagesByIds($ids: [ID!]!) {
    workPackagesByIds(ids: $ids) {
      id
      tag
      bookUrl
      allDocuments {
        documentNumber
        url
        commodity {
          weld {
            id
            tag
          }
          materialCommodity {
            id
            tag
          }
        }
      }
      attachments {
        id
        fileName
        url
        attachmentType
        fileSize
        createdAt
        creator {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const GET_WORKPACKAGE_BOOK_BUILDER_BY_IDS = gql`
  query workPackagesByIds($ids: [ID!]!) {
    workPackagesByIds(ids: $ids) {
      id
      bookUrl
    }
  }
`;

export const UPDATE_WORK_PACKAGE_SCHEDULE = gql`
  mutation updateWorkPackageSchedule($input: UpdateWorkPackageScheduleInput!) {
    updateWorkPackageSchedule(input: $input) {
      workPackages {
        id
        startDate
        endDate
        plannedMilestoneStartDate
        plannedMilestoneEndDate
      }
      unplannedMilestones {
        id
        name
        isExpired
        hours
        plannedStartDate
        plannedEndDate
        currentStatus {
          id
          codeI18n
          rejectionType {
            id
            reason
          }
          comments
          createdAt
          createdBy {
            id
            firstName
            lastName
            imageUrl
          }
        }
        quantity {
          ...Quantity
        }
        commodity {
          materialCommodity {
            id
            tag
            workPackage {
              id
              tag
              plannedHours
              plannedMilestoneStartDate
              plannedMilestoneEndDate
            }
            type {
              id
              name
            }
            material {
              mwrs {
                mwrNumber
                mwrDate
                mwrStatus
              }
              mrrNumber
              mrrDate
              deliveryDate
            }
          }
          weld {
            id
            tag
            workPackage {
              id
              tag
              plannedHours
              plannedMilestoneStartDate
              plannedMilestoneEndDate
            }
            type {
              id
              name
            }
          }
        }
      }
      erroredWorkPackages {
        errorCode
        workPackageId
      }
    }
    ${workPackageFragments.quantity}
  }
`;

export const SUBSCRIPTION_WORKPACKAGE_UPDATED = gql`
  subscription onWorkPackagesUpdated {
    workPackagesUpdated {
      workPackageIds
      affectedOwnerIds
      projectId
    }
  }
`;

export const UPDATE_WORKPACKAGE_DISCIPLINES = gql`
  mutation updateWorkPackageDisciplines($input: [UpdateWorkPackageDisciplineInput]!) {
    updateWorkPackageDisciplines(input: $input) {
      disciplines {
        id
        isActive
      }
      error
    }
  }
`;
export const GET_WORKPACKAGE_MILESTONE_STATS = gql`
  query workPackagesByIds($ids: [ID!]!, $input: MilestonesActivityStatsFilterInput!) {
    workPackagesByIds(ids: $ids) {
      id
      tag
      milestoneActivityStats(input: $input) {
        commodityType {
          id
          name
        }
        milestoneActivityStats {
          milestoneActivityStatus
          stats {
            milestoneType {
              id
              name
            }
            numMilestones
          }
        }
      }
    }
  }
`;

export const GET_WORKPACKAGE_QUANTITY_DETAILS = gql`
  query workpackageQuantityDetails($ids: [ID!]!) {
    workPackagesByIds(ids: $ids) {
      id
      earnedHours
      totalEarnableHours
      earnedQuantity
      totalEarnableQuantity {
        ...Quantity
      }
      quantityDetails {
        primaryOwnersDetail {
          owners {
            ...Owners
          }
          totalQuantity {
            ...QuantitySummary
          }
        }
        secondaryDayShiftOwnersDetail {
          owners {
            ...Owners
          }
          totalQuantity {
            ...QuantitySummary
          }
        }
        secondaryNightShiftOwnersDetail {
          owners {
            ...Owners
          }
          totalQuantity {
            ...QuantitySummary
          }
        }
        twQuantitySummary {
        ...QuantitySummary
      }
        totalQuantity {
          ...QuantitySummary
        }
      }
    }
      ${workpackageOwnersFragments.owner},
      ${workPackageQuantityFragment.quantitySummary},
      ${workPackageFragments.quantity},
      ${workPackageQuantityFragment.crewMember},
  }
`;

export const SUBSCRIPTION_WORK_PACKAGES_IMPORT = gql`
  subscription workPackagesImport {
    workPackagesImport {
      importStatus
    }
  }
`;

export const DELETE_WORK_PACKAGE_ATTACHMENT = gql`
  mutation deleteWorkPackageAttachment($input: DeleteWorkPackageAttachmentInput!) {
    deleteWorkPackageAttachment(input: $input) {
      error
      workPackage {
        id
        attachments {
          id
        }
      }
    }
  }
`;
